import { useMemo, useState } from "react";
import {
  grossProfit,
  QUADRANT_LABEL,
  SERVICE_PICKS,
  specTotals,
  type ServicePick,
  type ServicePickItem,
  type ItemKind,
  type Spec,
  type SpecOverride,
  type SpecStatus,
} from "@baros/core";
import type { Library } from "./api.js";
import { money, pct, quadrantColour, shortDate, STATUS_LABEL, STATUS_ORDER, statusColour } from "./format.js";

interface Props {
  readonly library: Library;
  readonly overrides: ReadonlyMap<string, SpecOverride>;
  /** Signatures and Enigmas from menu engineering, by spec; empty when there are none to offer. */
  readonly picks: ReadonlyMap<string, ServicePickItem>;
  readonly canEdit: boolean;
  readonly onOpen: (specId: string) => void;
  readonly onNew: (name: string, kind: ItemKind) => Promise<void>;
}

const KIND_HEADING: Readonly<Record<ItemKind, string>> = { drink: "Drinks", food: "Food" };

const ALL = "all";

// What a bartender needs to know to use them, in service terms. The margin
// side of the quadrant is the manager's business, on screen 07, and stays
// off this screen: everyone with the library sees these tags.
const PICK_HINT: Readonly<Record<ServicePick, string>> = {
  star: "House signatures: recommend these first",
  puzzle: "For guests after something a little different",
};

export function LibraryScreen({ library, overrides, picks, canEdit, onOpen, onNew }: Props) {
  const [status, setStatus] = useState<SpecStatus | typeof ALL>("available");
  const [style, setStyle] = useState<string>(ALL);
  // Multi-select, unlike style: combining is the point of tags. "batch" and
  // "signature" together is a question a bar actually asks; "Short and Sour"
  // is not, which is why the style row below stays single-select.
  const [tags, setTags] = useState<ReadonlySet<string>>(() => new Set());
  // Signature and Enigma: the tags menu engineering writes. One at a time —
  // a drink is in one quadrant at most, so choosing one swaps out the other,
  // the way "All tags" gives way to a tag — but either still narrows with the
  // ordinary tags, so "Signature" and "short" works.
  const [pick, setPick] = useState<ServicePick | null>(null);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState<ItemKind>("drink");
  const [err, setErr] = useState<string | null>(null);

  const ingredients = useMemo(
    () => new Map(library.ingredients.map((i) => [i.ingredientId, i])),
    [library.ingredients],
  );

  // One row per item (#180): its House recipe carries the figures, and its
  // variants are tabs on the item's page rather than rows here. A variant
  // whose House recipe is missing still gets a row, so nothing is lost.
  const kindOf = useMemo(() => new Map((library.items ?? []).map((i) => [i.itemId, i.kind])), [library.items]);
  const recipeCount = useMemo(() => {
    const n = new Map<string, number>();
    for (const s of library.specs) n.set(s.variantOf ?? s.specId, (n.get(s.variantOf ?? s.specId) ?? 0) + 1);
    return n;
  }, [library.specs]);
  const rows = useMemo(() => {
    const houseIds = new Set(library.specs.filter((s) => !s.variantOf).map((s) => s.specId));
    return library.specs.filter((spec) => !spec.variantOf || !houseIds.has(spec.variantOf)).map((spec) => {
      const t = specTotals(spec, ingredients);
      const price = overrides.get(spec.specId)?.price ?? null;
      return {
        spec,
        volume: t.volumeMl,
        cost: t.cost,
        abv: t.abv,
        gp: price ? grossProfit(price, t.cost) : null,
        price,
        unknown: t.unknownIngredientIds.length,
        kind: kindOf.get(spec.specId) ?? ("drink" as ItemKind),
        recipes: recipeCount.get(spec.specId) ?? 1,
      };
    });
  }, [library.specs, ingredients, overrides, kindOf, recipeCount]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { [ALL]: rows.length };
    for (const s of STATUS_ORDER) c[s] = rows.filter((r) => r.spec.status === s).length;
    return c;
  }, [rows]);

  const styles = useMemo(() => {
    const s = new Map<string, number>();
    for (const r of rows) for (const st of r.spec.style) s.set(st, (s.get(st) ?? 0) + 1);
    return [...s.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows]);

  /**
   * Every tag an item is filed under, its variants' own included (#188).
   *
   * A row here is an item, and a recipe carries its own tags now, so a tag
   * on the mezcal variant alone belongs to the Negroni as far as finding it
   * goes. Counting only the primary's would leave such a tag off the chips
   * entirely and unfindable.
   */
  const familyTags = useMemo(() => {
    const byPrimary = new Map<string, Set<string>>();
    for (const spec of library.specs) {
      const primary = spec.variantOf ?? spec.specId;
      const held = byPrimary.get(primary) ?? new Set<string>();
      for (const tag of spec.tags ?? []) held.add(tag);
      byPrimary.set(primary, held);
    }
    return byPrimary;
  }, [library.specs]);

  const tagCounts = useMemo(() => {
    const t = new Map<string, number>();
    for (const r of rows) for (const tag of familyTags.get(r.spec.specId) ?? []) t.set(tag, (t.get(tag) ?? 0) + 1);
    return [...t.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [rows, familyTags]);

  /**
   * A brand-new organisation starts with nothing: no catalogue, no specs,
   * its own book imported later as a separate exercise. That is not a
   * failure and not a filter that matched nothing, so when there is no book
   * at all the chips, the search and the table all come off and the screen
   * says what is true and what would fill it.
   */
  const empty = library.specs.length === 0;

  const q = query.trim().toLowerCase();
  const filtered = rows
    .filter((r) => status === ALL || r.spec.status === status)
    .filter((r) => style === ALL || r.spec.style.includes(style))
    // Every selected tag must match, not any: narrowing is what a second
    // chip is for.
    .filter((r) => [...tags].every((t) => familyTags.get(r.spec.specId)?.has(t) ?? false))
    .filter((r) => {
      const quadrant = picks.get(r.spec.specId)?.quadrant;
      return pick === null || quadrant === pick;
    })
    .filter(
      (r) =>
        !q ||
        r.spec.name.toLowerCase().includes(q) ||
        r.spec.lines.some((l) => ingredients.get(l.ingredientId)?.name.toLowerCase().includes(q)) ||
        // A variant's label or build counts for its item: "mezcal" finds the Negroni with a mezcal recipe.
        library.specs.some(
          (v) =>
            v.variantOf === r.spec.specId &&
            (v.name.toLowerCase().includes(q) || v.lines.some((l) => ingredients.get(l.ingredientId)?.name.toLowerCase().includes(q))),
        ),
    );
  // Each primary with its variants underneath, the primaries by status then
  // name. A variant whose primary the filters hid is listed on its own and
  // says whose variant it is.
  const byStatus = (a: Spec, b: Spec) => {
    const oa = STATUS_ORDER.indexOf(a.status);
    const ob = STATUS_ORDER.indexOf(b.status);
    return oa !== ob ? oa - ob : a.name.localeCompare(b.name);
  };
  // Asked for a recommendation, the best seller should be first, so a pick
  // lists flat by units a week rather than by status.
  const shown =
    pick !== null
      ? [...filtered].sort(
          (a, b) =>
            (picks.get(b.spec.specId)?.unitsPerWeek ?? 0) - (picks.get(a.spec.specId)?.unitsPerWeek ?? 0) ||
            a.spec.name.localeCompare(b.spec.name),
        )
      : [...filtered].sort((a, b) => byStatus(a.spec, b.spec));
  // Drinks first, then food, each under its own heading when both are present.
  const kinds = (["drink", "food"] as const).filter((k) => shown.some((r) => r.kind === k));

  const pickCounts = useMemo(() => {
    const c: Record<ServicePick, number> = { star: 0, puzzle: 0 };
    for (const p of picks.values()) c[p.quadrant] += 1;
    return c;
  }, [picks]);

  return (
    <div className="screen">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div className="label">
            {empty ? "nothing on file" : `${rows.length} items, ${library.specs.length} recipes on file`}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
            {empty ? "Empty" : `${shown.length} shown`}
          </div>
        </div>
        <span className="spacer" />
        {!empty && (
          <input
            type="text"
            placeholder="Search items, recipes or ingredients"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="lib-search"
          />
        )}
        {canEdit && !creating && (
          <button className="btn dashed" style={{ height: 56 }} onClick={() => setCreating(true)}>
            {empty ? "+ Add the first item" : "+ New item"}
          </button>
        )}
        {creating && (
          <form
            className="row"
            onSubmit={async (e) => {
              e.preventDefault();
              setErr(null);
              try {
                await onNew(newName, newKind);
                setCreating(false);
                setNewName("");
              } catch (error) {
                setErr(error instanceof Error ? error.message : "Could not create the item");
              }
            }}
          >
            <input
              type="text"
              autoFocus
              placeholder="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
            />
            <select value={newKind} onChange={(e) => setNewKind(e.target.value as ItemKind)} aria-label="Kind">
              <option value="drink">Drink</option>
              <option value="food">Food</option>
            </select>
            <button className="btn" type="submit">Create</button>
            <button className="btn" type="button" onClick={() => setCreating(false)}>Cancel</button>
          </form>
        )}
      </div>
      {err && <div style={{ padding: "0 20px 10px" }}><div className="msg err">{err}</div></div>}

      {empty ? (
        <div className="pad">
          <div className="msg">
            <div style={{ fontWeight: 600 }}>This organisation's recipe book is empty.</div>
            <div style={{ color: "var(--t2)", marginTop: 4 }}>
              {library.ingredients.length === 0
                ? "No ingredients either, so a spec would have nothing to cost against. Your book and your ingredient list are imported by Venox as their own exercise; the flavour wheel, menu engineering and the stocktake sheet stay empty until they are."
                : `${library.ingredients.length} ingredient${library.ingredients.length === 1 ? "" : "s"} are on file and no specs are. Venox imports your book as its own exercise${canEdit ? ", or write the first spec here" : ""}.`}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="row" style={{ padding: "0 20px 10px" }}>
            <span className="label" style={{ width: 52, flex: "0 0 52px" }}>Status</span>
            <div className="row chip-row">
              {[ALL, ...STATUS_ORDER].map((s) => (
                <button key={s} className="btn" aria-pressed={status === s} onClick={() => setStatus(s as SpecStatus | typeof ALL)}>
                  <span className="dot" style={{ background: s === ALL ? "transparent" : statusColour(s as SpecStatus) }} />
                  {s === ALL ? "All" : STATUS_LABEL[s as SpecStatus]}
                  <span className="num muted" style={{ fontSize: 11, letterSpacing: 0 }}>{counts[s]}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="row" style={{ padding: "0 20px 12px" }}>
            <span className="label" style={{ width: 52, flex: "0 0 52px" }}>Style</span>
            <div className="row chip-row">
              <button className="btn" aria-pressed={style === ALL} onClick={() => setStyle(ALL)}>
                All styles <span className="num muted" style={{ fontSize: 11, letterSpacing: 0 }}>{rows.length}</span>
              </button>
              {styles.map(([s, n]) => (
                <button key={s} className="btn" aria-pressed={style === s} onClick={() => setStyle(style === s ? ALL : s)}>
                  {s} <span className="num muted" style={{ fontSize: 11, letterSpacing: 0 }}>{n}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Only when something carries one — an org whose book predates
              tagging should not get an empty filter row asking about it.
              Signature and Enigma count: they need sales, not tags. */}
          {(tagCounts.length > 0 || picks.size > 0) && (
            <div className="row" style={{ padding: "0 20px 12px" }}>
              <span className="label" style={{ width: 52, flex: "0 0 52px" }}>Tags</span>
              <div className="row chip-row">
                <button
                  className="btn"
                  aria-pressed={tags.size === 0 && pick === null}
                  onClick={() => {
                    setTags(new Set());
                    setPick(null);
                  }}
                >
                  All tags <span className="num muted" style={{ fontSize: 11, letterSpacing: 0 }}>{rows.length}</span>
                </button>
                {picks.size > 0 &&
                  SERVICE_PICKS.map((q) => (
                    <button
                      key={q}
                      className="btn"
                      title={PICK_HINT[q]}
                      aria-pressed={pick === q}
                      onClick={() => setPick((prev) => (prev === q ? null : q))}
                    >
                      <span className="dot" style={{ background: quadrantColour(q) }} />
                      {QUADRANT_LABEL[q]} <span className="num muted" style={{ fontSize: 11, letterSpacing: 0 }}>{pickCounts[q]}</span>
                    </button>
                  ))}
                {tagCounts.map(([t, n]) => (
                  <button
                    key={t}
                    className="btn"
                    aria-pressed={tags.has(t)}
                    onClick={() =>
                      setTags((prev) => {
                        const next = new Set(prev);
                        if (!next.delete(t)) next.add(t);
                        return next;
                      })
                    }
                  >
                    {t} <span className="num muted" style={{ fontSize: 11, letterSpacing: 0 }}>{n}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="scroll" style={{ padding: "0 20px 20px" }}>
            <div className="table">
              <div className="thead">
                <span className="c-dot" />
                <span className="c-name">Item</span>
                <span className="c-status">Status</span>
                <span className="c-build">Build</span>
                <span className="c-price">Price</span>
                <span className="c-cost">Cost</span>
                <span className="c-gp">GP</span>
                <span className="c-abv">ABV</span>
                <span className="c-upd">Updated</span>
              </div>
              {kinds.map((kind) => (
                <div key={kind}>
                  {kinds.length > 1 && <div className="label" style={{ padding: "14px 0 6px" }}>{KIND_HEADING[kind]}</div>}
                  {shown.filter((r) => r.kind === kind).map((r) => (
                <button key={r.spec.specId} className="trow" onClick={() => onOpen(r.spec.specId)}>
                  <span className="c-dot"><span className="dot" style={{ background: statusColour(r.spec.status) }} /></span>
                  <span className="c-name">
                    <span className="n">{r.spec.name}</span>
                    <span className="s">
                      {r.recipes > 1 && <span className="fam">{r.recipes} recipes ·</span>}
                      {r.spec.style.join(" · ") || "—"}
                      {r.spec.glass && !r.spec.style.some((s) => s.toLowerCase() === r.spec.glass.toLowerCase()) && (
                        <span>· {r.spec.glass}</span>
                      )}
                      {!r.spec.reviewed && <span className="warn">· unreviewed import</span>}
                      {pick !== null && picks.has(r.spec.specId) && (
                        <span>
                          · {QUADRANT_LABEL[picks.get(r.spec.specId)!.quadrant]} · {Math.round(picks.get(r.spec.specId)!.unitsPerWeek)}/wk
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="c-status" style={{ color: statusColour(r.spec.status) }}>{STATUS_LABEL[r.spec.status]}</span>
                  <span className="c-build">{r.spec.lines.length} lines · {r.volume.toFixed(0)} mL</span>
                  <span className="c-price">{r.price !== null ? money(r.price) : "—"}</span>
                  <span className="c-cost">{money(r.cost)}{r.unknown ? "*" : ""}</span>
                  <span className="c-gp">{r.gp !== null ? pct(r.gp) : "—"}</span>
                  <span className="c-abv">{pct(r.abv)}</span>
                  <span className="c-upd">{shortDate(r.spec.updatedAt)}</span>
                </button>
                  ))}
                </div>
              ))}
              {shown.length === 0 && <div className="pad label">Nothing matches those filters.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
